import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ValidationHelperService } from "../../../../core/helpers/validation-helper.service";
import {EAction} from "../../../../shared/enums/EAction";
import {Subscription} from "rxjs";
import {RegularExerciseConfigFormFactory} from "../../../../core/forms/factory/RegularExerciseFormFactory";
import {ECompanionMode} from "../../../../shared/enums/ECompanionMode";
import {distinctUntilChanged, skip, startWith} from "rxjs/operators";

@Component({
  selector: "app-exercise-config-form",
  templateUrl: "./exercise-config-form.component.html",
  styleUrls: ["./exercise-config-form.component.scss"],
})
export class ExerciseConfigFormComponent implements OnInit, OnDestroy {
  @Input() _formGroup: FormGroup;
  @Input() _formControl: FormControl;
  @Input() action: EAction = EAction.Add;
  @Input() resetOnTypeChange = true;
  private typeSubscription?: Subscription;
  protected readonly ValidationHelperService = ValidationHelperService;

  ngOnInit(): void {
    this.typeSubscription = this.type.valueChanges.pipe(
      startWith(this.type.value),
      distinctUntilChanged(),
      skip(1),
    ).subscribe((type: number) => {
      if (this.action !== EAction.Add || !this.resetOnTypeChange) return;
      const controlName = [
        "countForm",
        "reactForm",
        "sequenceForm",
        "agilityForm",
        "verticalForm",
        "companionForm",
      ][type];
      if (!controlName) return;
      const defaults = RegularExerciseConfigFormFactory.create();
      this._formGroup.setControl("difficulty", defaults.controls.difficulty);
      this._formGroup.setControl(
        "countdownForm",
        defaults.controls.countdownForm,
      );
      this._formGroup.setControl(controlName, defaults.get(controlName));
    });
  }

  ngOnDestroy(): void {
    this.typeSubscription?.unsubscribe();
  }

  get difficulty(): FormControl {
    return this._formGroup.get("difficulty") as FormControl;
  }

  get numberOfLegs(): FormControl {
    return this.verticalForm.get("numberOfLegs") as FormControl;
  }

  get reactTo(): boolean[] {
    return this.reactForm.get("reactTo").value;
  }

  get react(): number[] {
    return this.reactTo
      .map((value, index) => (value ? index : null))
      .filter((value) => value !== null);
  }

  get dontReactTo(): boolean[] {
    return this.reactForm.get("dontReactTo").value;
  }

  get verticalForm(): FormGroup {
    return this._formGroup.get("verticalForm") as FormGroup;
  }

  get countdownsForm(): FormGroup {
    return this._formGroup.get("countdownForm") as FormGroup;
  }

  get startType(): FormControl {
    return this.countdownsForm.get("startType") as FormControl;
  }

  get startValue(): FormControl {
    return this.countdownsForm.get("startValue") as FormControl;
  }

  get endType(): FormControl {
    return this.countdownsForm.get("endType") as FormControl;
  }

  get endValue(): FormControl {
    return this.countdownsForm.get("endValue") as FormControl;
  }

  get reactForm(): FormGroup {
    return this._formGroup.get("reactForm") as FormGroup;
  }

  get neurocognitiveType(): FormControl {
    return this.reactForm.get("neurocognitiveType") as FormControl;
  }

  get countForm(): FormGroup {
    return this._formGroup.get("countForm") as FormGroup;
  }

  get countType(): FormControl {
    return this.countForm.get("countType") as FormControl;
  }

  get sequenceForm(): FormGroup {
    return this._formGroup.get("sequenceForm") as FormGroup;
  }

  get agilityForm(): FormGroup {
    return this._formGroup.get("agilityForm") as FormGroup;
  }

  get companionForm(): FormGroup {
    return this._formGroup.get("companionForm") as FormGroup;
  }

  get companionMode(): FormControl<ECompanionMode> {
    return this.companionForm.get("companionMode") as FormControl<ECompanionMode>;
  }

  get title(): FormControl {
    return this._formGroup.get("name") as FormControl;
  }

  get videoUrl(): FormControl {
    return this._formGroup.get("videoUrl") as FormControl;
  }

  get activeSensors(): boolean[] {
    return this.reactForm.get("activeSensors").value;
  }

  get timerPrompt(): FormControl {
    return this.reactForm.get("timerPrompt") as FormControl;
  }

  get sequenceSensors(): number[] {
    return this.sequenceForm.get("sequenceSensors").value;
  }

  get isolatedReact(): FormControl {
    return this.reactForm.get("isolatedReact") as FormControl;
  }

  get type(): FormControl {
    return this._formControl;
  }
}
